import { cardFields, createCard, deserializeCard, LOCAL, serializeCard } from "@mn/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import * as Y from "yjs";
import { useSession } from "@/app/session";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { BoardContext, type BoardCtx } from "./BoardContext";
import { NoteBody } from "./cards/NoteCard";
import { type BoardHandle, openBoard, presenceColor } from "./collab";
import { useMapKeys } from "./hooks";
import { Interactions } from "./interactions";
import { SpatialIndex } from "./spatial";
import { useBoardUI } from "./store";

/**
 * Personal capture inbox: a hidden per-user board whose notes are listed here. Notes can be
 * written anywhere and later sent onto the board currently open.
 */
export function QuickNotesPanel({ target, onClose }: { target: BoardCtx | null; onClose: () => void }) {
  const t = useT();
  const { me } = useSession();
  const [ctx, setCtx] = useState<BoardCtx | null>(null);
  const [text, setText] = useState("");
  const input = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!me) return;
    let handle: BoardHandle | null = null;
    let cancelled = false;
    const user = {
      id: me.user.id,
      name: me.user.name,
      image: me.user.image,
      color: presenceColor(me.user.id),
    };
    api<{ boardId: string }>("/api/quick-notes")
      .then(({ boardId }) => {
        if (cancelled) return;
        handle = openBoard(boardId, user, { readOnly: false });
        const index = new SpatialIndex(handle.types.cards);
        setCtx({
          boardId,
          handle,
          index,
          interactions: new Interactions(document.body, handle, index, user.id, true),
          role: "editor",
          canEdit: true,
          user,
          openBoard: () => {},
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      handle?.close();
    };
  }, [me]);
  useEffect(() => {
    input.current?.focus();
  }, []);

  const add = () => {
    const body = text.trim();
    if (!ctx || !body) return;
    const id = createCard(ctx.handle.doc, { type: "note", x: 0, y: 0, userId: ctx.user.id });
    const frag = ctx.handle.types.cards.get(id)?.get("body");
    if (frag instanceof Y.XmlFragment)
      ctx.handle.doc.transact(() => {
        frag.insert(
          0,
          body.split(/\r?\n/).map((line) => {
            const p = new Y.XmlElement("paragraph");
            if (line) p.insert(0, [new Y.XmlText(line)]);
            return p;
          }),
        );
      }, LOCAL);
    setText("");
  };

  return (
    <aside className="absolute bottom-0 right-0 top-0 z-30 flex flex-col border-l border-border bg-panel shadow-panel max-md:w-full md:w-96">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <span className="display text-xl">.{t.quickNotes}</span>
        <button type="button" onClick={onClose} className="text-muted hover:text-text">
          ✕
        </button>
      </div>
      <div className="border-b border-border p-3">
        <textarea
          ref={input}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              add();
            }
          }}
          placeholder={t.quickNotePlaceholder}
          rows={2}
          className="w-full resize-none rounded-sm border border-border bg-panel-2 px-2 py-1.5 text-sm text-text outline-none placeholder:text-dim"
        />
        <div className="mt-1 text-[11px] text-dim">{t.quickNotesHint}</div>
      </div>
      {ctx ? (
        <BoardContext.Provider value={ctx}>
          <List ctx={ctx} target={target} />
        </BoardContext.Provider>
      ) : (
        <div className="p-4 text-sm text-dim">{t.loading}</div>
      )}
    </aside>
  );
}

function List({ ctx, target }: { ctx: BoardCtx; target: BoardCtx | null }) {
  const t = useT();
  const ids = useMapKeys(ctx.handle.types.cards);
  const ordered = useMemo(
    () =>
      ids
        .map((id) => ({ id, m: ctx.handle.types.cards.get(id)! }))
        .filter((x) => x.m && x.m.get("type") === "note")
        .sort((a, b) => ((b.m.get("createdAt") as number) ?? 0) - ((a.m.get("createdAt") as number) ?? 0)),
    [ids, ctx],
  );
  const sendHere = (id: string) => {
    if (!target?.canEdit) return;
    const src = ctx.handle.types.cards.get(id);
    if (!src) return;
    const { camera, viewport } = useBoardUI.getState();
    const f = cardFields(src);
    const nid = deserializeCard(target.handle.doc, serializeCard(src), {
      userId: target.user.id,
      x: Math.round(viewport.w / 2 / camera.z - camera.x - f.w / 2),
      y: Math.round(viewport.h / 2 / camera.z - camera.y - 60),
      parent: null,
      unsorted: false,
    });
    ctx.handle.doc.transact(() => ctx.handle.types.cards.delete(id), LOCAL);
    useBoardUI.getState().select([nid]);
  };
  const remove = (id: string) => ctx.handle.doc.transact(() => ctx.handle.types.cards.delete(id), LOCAL);
  return (
    <ul className="flex-1 overflow-auto p-3">
      {ordered.length === 0 ? <li className="text-sm text-dim">{t.quickNotesEmpty}</li> : null}
      {ordered.map(({ id, m }) => (
        <li key={id} className="mb-3 rounded-[2px] bg-[#2b2b2b] shadow-[0_1px_4px_rgba(0,0,0,.5)]">
          <div
            className="cursor-text"
            onClick={() => useBoardUI.getState().setEditing(id)}
            onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && useBoardUI.getState().setEditing(id)}
          >
            <NoteBody id={id} fragment={m.get("body") as Y.XmlFragment} fg="#f2f2f2" />
          </div>
          <div className="flex items-center justify-end gap-2 border-t border-border/60 px-2 py-1 text-[11px]">
            {target?.canEdit ? (
              <button type="button" onClick={() => sendHere(id)} className="text-muted hover:text-text">
                {t.sendToThisBoard}
              </button>
            ) : null}
            <button type="button" onClick={() => remove(id)} className="text-dim hover:text-error">
              {t.delete}
            </button>
          </div>
        </li>
      ))}
    </ul>
  );
}
